/* Atelier — global.js : dialogs, en-tête, méga-menu, recherche instantanée, panier AJAX, analytics custom.
   Web Components natifs, aucune dépendance. Chargé en defer. */

(() => {
  const theme = window.theme || {};
  const mqDesktop = window.matchMedia('(min-width: 990px)');

  const announce = (message) => {
    const region = document.getElementById('LiveRegion');
    if (!region) return;
    region.textContent = '';
    window.setTimeout(() => { region.textContent = message; }, 50);
  };

  const publish = (name, data) => {
    try {
      if (window.Shopify && Shopify.analytics && typeof Shopify.analytics.publish === 'function') {
        Shopify.analytics.publish(`atelier:${name}`, data);
      }
    } catch (e) { /* analytics ne doit jamais casser l'UX */ }
  };

  /* ---------- Dialogs (menu mobile, recherche, panier) ---------- */
  const openDialog = (id, trigger) => {
    const dialog = document.getElementById(id);
    if (!dialog || dialog.open) return;
    dialog.showModal();
    dialog._trigger = trigger || document.activeElement;
    if (trigger) trigger.setAttribute('aria-expanded', 'true');
    const autofocus = dialog.querySelector('input[type="search"]');
    if (autofocus) autofocus.focus();
  };

  const closeDialog = (dialog) => {
    if (!dialog || !dialog.open) return;
    dialog.close();
  };

  document.addEventListener('click', (event) => {
    const opener = event.target.closest('[data-open-dialog]');
    if (opener) {
      event.preventDefault();
      openDialog(opener.dataset.openDialog, opener);
      return;
    }
    const closer = event.target.closest('[data-close-dialog]');
    if (closer) {
      closeDialog(closer.closest('dialog'));
      return;
    }
    if (event.target instanceof HTMLDialogElement && event.target.open) {
      const rect = event.target.getBoundingClientRect();
      const inside = event.clientX >= rect.left && event.clientX <= rect.right && event.clientY >= rect.top && event.clientY <= rect.bottom;
      if (!inside) closeDialog(event.target);
    }
  });

  document.addEventListener('close', (event) => {
    const dialog = event.target;
    if (!(dialog instanceof HTMLDialogElement)) return;
    if (dialog._trigger) {
      dialog._trigger.setAttribute('aria-expanded', 'false');
      if (document.contains(dialog._trigger)) dialog._trigger.focus();
    }
  }, true);

  /* ---------- Sticky header (se masque au scroll bas) ---------- */
  class StickyHeader extends HTMLElement {
    connectedCallback() {
      if (this.dataset.sticky !== 'true') return;
      this.lastY = window.scrollY;
      this.onScroll = this.onScroll.bind(this);
      window.addEventListener('scroll', this.onScroll, { passive: true });
    }
    disconnectedCallback() { window.removeEventListener('scroll', this.onScroll); }
    onScroll() {
      if (this.ticking) return;
      this.ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const menuOpen = this.querySelector('[aria-expanded="true"]');
        this.classList.toggle('is-hidden', y > this.lastY && y > 240 && !menuOpen);
        this.lastY = y;
        this.ticking = false;
      });
    }
  }
  customElements.define('sticky-header', StickyHeader);

  /* ---------- Méga-menu : clic, survol (desktop), Échap ---------- */
  class MegaMenu extends HTMLElement {
    connectedCallback() {
      this.button = this.querySelector('button');
      this.panel = this.querySelector('.mega__panel');
      this.button.addEventListener('click', () => this.toggle());
      this.addEventListener('mouseenter', () => { if (mqDesktop.matches) { clearTimeout(this.timer); this.timer = setTimeout(() => this.open(), 80); } });
      this.addEventListener('mouseleave', () => { clearTimeout(this.timer); this.timer = setTimeout(() => this.close(), 160); });
      this.addEventListener('keydown', (e) => { if (e.key === 'Escape') { this.close(); this.button.focus(); } });
      this.addEventListener('focusout', (e) => { if (!this.contains(e.relatedTarget)) this.close(); });
    }
    toggle() { this.button.getAttribute('aria-expanded') === 'true' ? this.close() : this.open(); }
    open() {
      document.querySelectorAll('mega-menu').forEach((m) => m !== this && m.close());
      this.button.setAttribute('aria-expanded', 'true');
      this.panel.hidden = false;
    }
    close() {
      this.button.setAttribute('aria-expanded', 'false');
      this.panel.hidden = true;
    }
  }
  customElements.define('mega-menu', MegaMenu);

  /* ---------- Accordéons du footer : fermés sur mobile ---------- */
  const syncFooter = () => {
    document.querySelectorAll('[data-footer-accordion]').forEach((d) => { d.open = mqDesktop.matches; });
  };
  syncFooter();
  mqDesktop.addEventListener('change', syncFooter);

  /* ---------- Recherche instantanée ---------- */
  class PredictiveSearch extends HTMLElement {
    connectedCallback() {
      this.input = document.getElementById(this.dataset.input);
      this.results = this.querySelector('.predictive-search');
      this.popular = this.closest('dialog')?.querySelector('[data-search-popular]');
      this.cache = new Map();
      if (!this.input) return;
      this.input.addEventListener('input', this.debounce(() => this.search(), 220));
      this.input.addEventListener('keydown', (e) => this.onKey(e));
    }
    debounce(fn, wait) { let t; return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(this, args), wait); }; }
    async search() {
      const q = this.input.value.trim();
      if (q.length < 2) { this.render(''); return; }
      if (this.cache.has(q)) { this.render(this.cache.get(q)); return; }
      this.controller?.abort();
      this.controller = new AbortController();
      const params = new URLSearchParams({
        q,
        section_id: 'predictive-search',
        'resources[type]': 'product,collection,query,article',
        'resources[limit]': '8',
        'resources[limit_scope]': 'each',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,variants.title,tag,vendor'
      });
      try {
        const res = await fetch(`${this.dataset.url}?${params}`, { signal: this.controller.signal });
        if (!res.ok) throw new Error(res.status);
        const html = await res.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const inner = doc.getElementById('PredictiveSearchResultsInner');
        const markup = inner ? inner.outerHTML : '';
        this.cache.set(q, markup);
        this.render(markup);
        publish('search_suggest', { search_term: q });
      } catch (e) {
        if (e.name !== 'AbortError') this.render('');
      }
    }
    render(markup) {
      this.results.innerHTML = markup;
      this.input.setAttribute('aria-expanded', markup ? 'true' : 'false');
      if (this.popular) this.popular.hidden = Boolean(markup);
      this.activeIndex = -1;
    }
    onKey(e) {
      const options = [...this.results.querySelectorAll('[role="option"]')];
      if (!options.length || !['ArrowDown', 'ArrowUp', 'Enter'].includes(e.key)) return;
      if (e.key === 'Enter') {
        if (this.activeIndex >= 0) { e.preventDefault(); options[this.activeIndex].querySelector('a')?.click(); }
        return;
      }
      e.preventDefault();
      const delta = e.key === 'ArrowDown' ? 1 : -1;
      this.activeIndex = (this.activeIndex + delta + options.length) % options.length;
      options.forEach((o, i) => o.setAttribute('aria-selected', i === this.activeIndex ? 'true' : 'false'));
      this.input.setAttribute('aria-activedescendant', options[this.activeIndex].id);
      options[this.activeIndex].scrollIntoView({ block: 'nearest' });
    }
  }
  customElements.define('predictive-search', PredictiveSearch);

  /* ---------- Panier AJAX ---------- */
  const CART_SECTIONS = ['cart-drawer', 'cart-icon-bubble'];

  const sectionInner = (html) => {
    const doc = new DOMParser().parseFromString(html, 'text/html');
    const wrapper = doc.querySelector('.shopify-section');
    return wrapper ? wrapper.innerHTML : html;
  };

  const renderCartSections = (sections) => {
    if (!sections) return;
    if (sections['cart-icon-bubble']) {
      const bubble = document.getElementById('cart-icon-bubble');
      if (bubble) bubble.innerHTML = sectionInner(sections['cart-icon-bubble']);
    }
    if (sections['cart-drawer']) {
      const drawer = document.getElementById('CartDrawer');
      const doc = new DOMParser().parseFromString(sections['cart-drawer'], 'text/html');
      const fresh = doc.getElementById('CartDrawerInner');
      const current = drawer?.querySelector('#CartDrawerInner');
      if (fresh && current) current.replaceWith(fresh);
    }
  };

  const cartRequest = async (url, body) => {
    const res = await fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ ...body, sections: CART_SECTIONS, sections_url: window.location.pathname })
    });
    const data = await res.json();
    if (!res.ok || data.status) {
      const error = new Error(data.description || data.message || theme.strings.cartError);
      error.data = data;
      throw error;
    }
    return data;
  };

  window.theme.cart = {
    async add(items, { openDrawer = true } = {}) {
      const data = await cartRequest(`${theme.routes.cartAdd}.js`, { items });
      if (theme.cartType === 'drawer' && document.getElementById('CartDrawer')) {
        renderCartSections(data.sections);
        if (openDrawer) openDialog('CartDrawer', document.getElementById('cart-icon-bubble'));
      } else {
        window.location.href = theme.routes.cart;
      }
      return data;
    },
    async change(line, quantity) {
      const data = await cartRequest(`${theme.routes.cartChange}.js`, { line, quantity });
      renderCartSections(data.sections);
      return data;
    }
  };

  /* Ouvre le tiroir au clic sur l'icône panier (lien /cart conservé sans JS) */
  document.addEventListener('click', (event) => {
    const toggle = event.target.closest('[data-cart-toggle]');
    if (!toggle || theme.cartType !== 'drawer' || !document.getElementById('CartDrawer')) return;
    event.preventDefault();
    openDialog('CartDrawer', toggle);
  });

  /* Quantités dans le tiroir / la page panier (délégation : survit aux re-rendus) */
  document.addEventListener('click', async (event) => {
    const button = event.target.closest('[data-cart-qty]');
    if (!button) return;
    event.preventDefault();
    const line = Number(button.dataset.line);
    const quantity = Number(button.dataset.cartQty);
    const container = button.closest('[data-cart-container]');
    container?.setAttribute('aria-busy', 'true');
    try {
      await window.theme.cart.change(line, quantity);
      if (container?.dataset.cartContainer === 'page') window.location.reload();
      announce(quantity === 0 ? (button.dataset.removedLabel || '') : (button.dataset.updatedLabel || ''));
    } catch (e) {
      const errorEl = container?.querySelector('[data-cart-error]');
      if (errorEl) { errorEl.textContent = e.message; errorEl.hidden = false; }
    } finally {
      container?.removeAttribute('aria-busy');
    }
  });

  /* ---------- Analytics : select_item (pixel personnalisé) ---------- */
  document.addEventListener('click', (event) => {
    const link = event.target.closest('[data-product-card] a');
    if (!link) return;
    const card = link.closest('[data-product-card]');
    publish('select_item', {
      item_list_name: card.dataset.listName || document.title,
      item_id: card.dataset.productId,
      item_name: card.dataset.productTitle,
      price: Number(card.dataset.productPrice || 0),
      index: Number(card.dataset.position || 0)
    });
  });

  /* ---------- Révélation au scroll (discrète, respecte reduced-motion via CSS) ---------- */
  if (document.body.dataset.animations === 'true' && 'IntersectionObserver' in window) {
    document.documentElement.classList.add('reveal-ready');
    const io = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) { entry.target.classList.add('is-visible'); io.unobserve(entry.target); }
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    document.querySelectorAll('.reveal').forEach((el) => io.observe(el));
  }
})();
